import React, { memo } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  TableContainer,
  Box,
  Badge,
  TableFooter,
  Chip,
} from "investira.react.components";
import { formats, validators } from "investira.sdk";
import { Link } from "react-router-dom";
import { ATIVO_TIPO } from "@investirapri/financa.js/lib/enums";

import { PessoaImage, NumberTableCell } from "../../../../components/molecules";
import { PATHS } from "../../../../enums";

const DetalheTable = memo((props) => {
  const { detalhes, quantidade_total, financeiro_total } = props.detalhe;

  /**
   * Agrupa o detalhe por tipo de ativo
   * Totaliza as possíveis divergências
   * @param {object} pDetalhe - Objeto a ser agrupado
   * @returns {Object} - Objeto com os dados agrupados e ordenados
   */
  function agrupaPorAtivoTipo(pDetalhe) {
    if (validators.isEmpty(pDetalhe)) {
      return {};
    }

    const xAtivosTipo = {};

    for (const [xKey, xAtivos] of Object.entries(pDetalhe)) {
      for (const xAtivo of xAtivos) {
        const xAtivoTipoId = xAtivo.ativo_tipo_id;
        // Se o tipo de ativo não existir no objeto, cria um objeto vazio para ele
        if (!xAtivosTipo[xAtivoTipoId]) {
          xAtivosTipo[xAtivoTipoId] = {
            detalhes: {},
            quantidade_total: 0,
            financeiro_total: 0,
          };
        }
        // Se o ativo não existir no objeto, cria um array e vazio para ele
        if (!xAtivosTipo[xAtivoTipoId].detalhes[xKey]) {
          xAtivosTipo[xAtivoTipoId].detalhes[xKey] = [];
        }
        xAtivosTipo[xAtivoTipoId].detalhes[xKey].push(xAtivo);
        // Totaliza quantidade e financeiro das possíveis divergências
        xAtivosTipo[xAtivoTipoId].quantidade_total +=
          xAtivo?.divergencias?.quantidade || 0;
        xAtivosTipo[xAtivoTipoId].financeiro_total +=
          xAtivo?.divergencias?.financeiro || 0;
      }
    }

    // Ordena os tipos de ativos
    const xAtivosTipoSorted = Object.fromEntries(
      Object.entries(xAtivosTipo).sort(([a], [b]) => {
        return a.localeCompare(b);
      }),
    );
    return xAtivosTipoSorted;
  }
  /**
   * Recupera o status do ativo
   * @param {Object} pAtivo - Dados do ativo
   * @returns {Object} - Status do ativo
   */
  function getStatus(pAtivo) {
    if (pAtivo.ignorado || pAtivo.ignorado_tolerancia) {
      return {
        text: "Ignorado",
        color: "info",
      };
    }
    if (validators.isEmpty(pAtivo.divergencias)) {
      return {
        text: "OK",
        color: "success",
      };
    }
    if (pAtivo.divergencias?.descricao.toLocaleUpperCase().includes("DIV")) {
      return {
        text: "Divergente",
        color: "warning",
      };
    }
    if (pAtivo.divergencias?.descricao.toLocaleUpperCase().includes("AUS")) {
      return {
        text: "Ausente",
        color: "error",
      };
    }
    return null;
  }

  return (
    <TableContainer
      id="table-container"
      sx={{
        overflow: "auto",
      }}
    >
      <Table size="small" sx={{ borderColor: "secondary" }}>
        <TableHead>
          <TableRow>
            <TableCell />
            <TableCell sx={{ verticalAlign: "bottom", width: "350px" }}>
              Ativo
            </TableCell>
            <TableCell sx={{ verticalAlign: "bottom", width: "150px" }}>
              ISIN
            </TableCell>
            <TableCell sx={{ verticalAlign: "bottom", width: "180px" }}>
              Operação Data
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: "bottom",
                width: "120px",
                textAlign: "right",
              }}
            >
              Taxa (%)
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
                width: "150px",
              }}
            >
              Quantidade
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
                width: "150px",
              }}
            >
              PU
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "right",
                width: "150px",
              }}
            >
              Financeiro
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "center",
                width: "100px",
              }}
            >
              Status
            </TableCell>
            <TableCell
              sx={{
                verticalAlign: "bottom",
                textAlign: "left",
                width: "250px",
              }}
            >
              Obs.
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(agrupaPorAtivoTipo(detalhes)).map(
            ([xAtivoTipoId, xDados]) => {
              const {
                detalhes: xDetalhes,
                quantidade_total,
                financeiro_total,
              } = xDados;
              return (
                <React.Fragment key={crypto.randomUUID()}>
                  {!validators.isNull(xAtivoTipoId) && (
                    <TableRow>
                      <TableCell
                        colSpan="5"
                        sx={{ pl: 4, color: "text.secondary", fontWeight: 600 }}
                      >
                        {ATIVO_TIPO[xAtivoTipoId]?.descricao}
                      </TableCell>
                      <NumberTableCell
                        value={quantidade_total || null}
                        decimal={6}
                        separateThousand={true}
                        sx={{ color: "text.secondary", fontWeight: 600 }}
                      />
                      <TableCell align="right" />
                      <NumberTableCell
                        value={financeiro_total || null}
                        decimal={2}
                        separateThousand={true}
                        sx={{ color: "text.secondary", fontWeight: 600 }}
                      />
                      <TableCell colSpan={2} />
                    </TableRow>
                  )}
                  {Object.entries(xDetalhes).map(([xKey, xAtivos], xIndex) => {
                    const xStatus = getStatus(xAtivos[0]);
                    const xMercado = ATIVO_TIPO[xAtivoTipoId]?.mercado;
                    const xPath =
                      xMercado && xAtivos[0]?.ifeed_ativo_id
                        ? `/ativos${PATHS[xMercado.toLowerCase()].path}/${
                            xAtivos[0]?.ifeed_ativo_id
                          }`
                        : null;

                    return (
                      <React.Fragment key={crypto.randomUUID()}>
                        <TableRow sx={{ backgroundColor: "secondary.dark" }}>
                          <TableCell sx={{ pl: 6, width: "24px" }}>
                            {Boolean(xAtivos[0]?.emissor?.logo_pessoa_id) && (
                              <Link
                                to={`/empresas/${xAtivos[0].emissor?.logo_pessoa_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <PessoaImage
                                  logoPessoaId={
                                    xAtivos[0].emissor?.logo_pessoa_id
                                  }
                                  alt={xAtivos[0].emissor?.pessoa_fantasia}
                                  hasLogo={Boolean(
                                    xAtivos[0]?.emissor?.logo_pessoa_id,
                                  )}
                                  size={24}
                                />
                              </Link>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge
                              badgeContent={xAtivos[0].marcacao}
                              color="warning"
                              anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                              }}
                            >
                              {xPath ? (
                                <Box pr={1.5}>
                                  <Typography
                                    component={Link}
                                    to={xPath}
                                    color="primary"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    variant="body2"
                                    aria-label={`Abrir ${xAtivos[0].ativo} em uma nova aba`}
                                  >
                                    {xAtivos[0].ativo}
                                  </Typography>
                                </Box>
                              ) : (
                                <Box pr={1.5}>
                                  <Typography
                                    color="textPrimary"
                                    variant="body2"
                                  >
                                    {xAtivos[0].ativo}
                                  </Typography>
                                </Box>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {xAtivos[0].isin || "---"}
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {(xAtivos[0]?.operacao_data &&
                              formats.formatDateCustom(
                                xAtivos[0].operacao_data,
                                "DD/MMM/YYYY",
                              )) ||
                              "---"}
                          </TableCell>
                          <TableCell colSpan={4} />
                          <TableCell align="center">
                            {xStatus && (
                              <Chip
                                label={xStatus.text}
                                color={xStatus.color}
                                size="small"
                              />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {xAtivos[0]?.ignorado_tolerancia && (
                              <span>
                                Ativo dentro da tolerância de quantidade
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                        {xAtivos.map((xAtivo) => {
                          return (
                            <React.Fragment key={crypto.randomUUID()}>
                              <TableRow>
                                <TableCell sx={{ pl: 6, width: "24px" }} />
                                <TableCell>Local</TableCell>
                                <TableCell />
                                <TableCell />
                                <NumberTableCell
                                  value={xAtivo.local?.operacao_taxa || null}
                                  decimal={4}
                                  separateThousand={false}
                                />
                                <NumberTableCell
                                  value={xAtivo.local?.quantidade || null}
                                  decimal={6}
                                  separateThousand={true}
                                />
                                <NumberTableCell
                                  value={xAtivo.local?.pu || null}
                                  decimal={6}
                                />
                                <NumberTableCell
                                  value={xAtivo.local?.financeiro || null}
                                  decimal={2}
                                  separateThousand={true}
                                />
                                <TableCell colSpan={2} />
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ pl: 6, width: "24px" }} />
                                <TableCell>Custodiante</TableCell>
                                <TableCell />
                                <TableCell />
                                <NumberTableCell
                                  value={
                                    xAtivo.custodiante?.operacao_taxa || null
                                  }
                                  decimal={4}
                                  separateThousand={false}
                                />
                                <NumberTableCell
                                  value={xAtivo.custodiante?.quantidade || null}
                                  decimal={6}
                                  separateThousand={true}
                                />
                                <NumberTableCell
                                  value={xAtivo.custodiante?.pu || null}
                                  decimal={6}
                                />
                                <NumberTableCell
                                  value={xAtivo.custodiante?.financeiro || null}
                                  decimal={2}
                                  separateThousand={true}
                                />
                                <TableCell colSpan={2} />
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ pl: 6, width: "24px" }} />
                                <TableCell sx={{ fontWeight: 600 }}>
                                  Diferença
                                </TableCell>
                                <TableCell />
                                <TableCell />
                                <NumberTableCell
                                  sx={{ fontWeight: 600 }}
                                  value={
                                    xAtivo.divergencias?.operacao_taxa || null
                                  }
                                  decimal={4}
                                  separateThousand={false}
                                />
                                <NumberTableCell
                                  sx={{ fontWeight: 600 }}
                                  value={
                                    xAtivo.divergencias?.quantidade || null
                                  }
                                  decimal={6}
                                  separateThousand={true}
                                />
                                <NumberTableCell
                                  sx={{ fontWeight: 600 }}
                                  value={xAtivo.divergencias?.pu || null}
                                  decimal={6}
                                />
                                <NumberTableCell
                                  sx={{ fontWeight: 600 }}
                                  value={
                                    xAtivo.divergencias?.financeiro || null
                                  }
                                  decimal={2}
                                  separateThousand={true}
                                />
                                <TableCell />
                                <TableCell sx={{ fontWeight: 600 }}>
                                  {xAtivo.divergencias?.descricao}
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            },
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TableCell colSpan={5} sx={{ verticalAlign: "bottom" }}></TableCell>
            <NumberTableCell
              value={quantidade_total || null}
              decimal={6}
              separateThousand={true}
              sx={{
                fontWeight: 600,
              }}
            />
            <TableCell align="right" />
            <NumberTableCell
              value={financeiro_total || null}
              decimal={2}
              separateThousand={true}
              sx={{
                fontWeight: 600,
              }}
            />
            <TableCell colSpan={2} />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
});

export default DetalheTable;
